import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardList, LogIn, LogOut, Star, XCircle, Plus, Search,
  Download, Columns3, EyeOff, MessageSquare, MapPin, AlertCircle,
  CheckCircle2,
} from "lucide-react";
import WebsiteAnalyticsCard from "./WebsiteAnalyticsCard";

const todayISO = () => new Date().toISOString().slice(0, 10);
const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

/**
 * Booking-extranet style "Homepage di Gruppo".
 * Aggregates the current property (B&B Emmanuel) data — ready for multi-struttura.
 */
export default function GroupHomeTab({ onAddProperty }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("activity"); // activity | performance | settings
  const [statusFilter, setStatusFilter] = useState("all");
  const [idQuery, setIdQuery] = useState("");
  // Filters: location (city) + property type. With a single property both are
  // effectively no-ops, but they are kept editable so the user can experiment
  // before adding more properties.
  const [locationFilter, setLocationFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [b, r, m] = await Promise.all([
          api.get("/admin/bookings"),
          api.get("/admin/reviews").catch(() => ({ data: { reviews: [] } })),
          api.get("/admin/messages").catch(() => ({ data: [] })),
        ]);
        setBookings(b.data || []);
        setReviews(r.data?.reviews || []);
        setMessages(m.data || []);
      } catch {
        toast.error("Errore nel caricamento");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const today = todayISO();
  const next48 = inDays(2);

  const metrics = useMemo(() => {
    const ACTIVE = ["confirmed", "completed", "checked_in"];
    const arrivals48 = bookings.filter((b) => ACTIVE.includes(b.status) && b.check_in >= today && b.check_in <= next48);
    const departures48 = bookings.filter((b) => ACTIVE.includes(b.status) && b.check_out >= today && b.check_out <= next48);
    return {
      bookingsToday: bookings.filter((b) => b.created_at?.startsWith(today)).length,
      arrivalsToday: bookings.filter((b) => b.check_in === today && ACTIVE.includes(b.status)).length,
      departuresToday: bookings.filter((b) => b.check_out === today && ACTIVE.includes(b.status)).length,
      reviewsToday: reviews.filter((r) => (r.created_at || "").startsWith(today)).length,
      cancellations: bookings.filter((b) => b.status === "cancelled" || b.status === "rejected").length,
      arrivals48: arrivals48.length,
      departures48: departures48.length,
      guestMsgs: messages.filter((m) => m.kind !== "flexible_request").length,
    };
  }, [bookings, reviews, messages, today, next48]);

  // Single property = B&B Emmanuel. Ready for multi-struttura: just push more rows.
  const properties = useMemo(() => ([
    {
      id: "367428",
      name: "B&B Emmanuel",
      address: "Via Padre Annibale Maria di Francia, 21 · Reggio Calabria, IT",
      city: "reggio_calabria",
      type: "bnb",
      status: "open",
      arrivals48: metrics.arrivals48,
      departures48: metrics.departures48,
      guestMsgs: metrics.guestMsgs,
      channelMsgs: 0,
    },
  ]), [metrics]);

  const filteredProps = properties.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (idQuery.trim() && !p.id.includes(idQuery.trim())) return false;
    if (locationFilter !== "all" && p.city !== locationFilter) return false;
    if (typeFilter !== "all" && p.type !== typeFilter) return false;
    return true;
  });

  return (
    <div className="space-y-5" data-testid="group-home-tab">
      {/* Page title */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl text-[#1E3A5F]">Homepage di Gruppo</h2>
      </div>

      {/* Website Analytics: site visits, chat opens, booking funnel */}
      <WebsiteAnalyticsCard />

      {/* FILTER BAR */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-4">
          <label className="text-[11px] text-[#635F59] uppercase tracking-wider font-semibold">Filtra per località</label>
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="mt-1 h-10" data-testid="group-location-filter">
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#AC5D45]" /><SelectValue placeholder="Tutte le località" /></div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte ({properties.length} {properties.length === 1 ? "struttura" : "strutture"})</SelectItem>
              <SelectItem value="reggio_calabria">Reggio Calabria</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-4">
          <label className="text-[11px] text-[#635F59] uppercase tracking-wider font-semibold">Filtra per tipologia di struttura</label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="mt-1 h-10" data-testid="group-type-filter"><SelectValue placeholder="Tutte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="bnb">B&amp;B</SelectItem>
              <SelectItem value="apartment">Appartamento</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-4">
          <label className="text-[11px] text-[#635F59] uppercase tracking-wider font-semibold">Filtra per ID della struttura</label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#635F59]" />
            <Input
              value={idQuery}
              onChange={(e) => setIdQuery(e.target.value)}
              placeholder="Cerca ID..."
              data-testid="group-id-filter"
              className="pl-9 h-10"
            />
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="border-b border-[#E8E2D9] flex gap-6 text-sm">
        {[
          { id: "activity", label: "Attività" },
          { id: "performance", label: "Performance" },
          { id: "settings", label: "Impostazioni" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`group-tab-${t.id}`}
            className={`pb-2 -mb-px transition border-b-2 ${tab === t.id ? "border-[#0071C2] text-[#0071C2] font-semibold" : "border-transparent text-[#635F59] hover:text-[#1E3A5F]"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "activity" && (
        <>
          {/* IN CORSO OGGI */}
          <div>
            <h3 className="font-display text-lg text-[#1E3A5F] mb-2">In corso oggi</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="group-metrics">
              <MetricCard icon={ClipboardList} label="Prenotazioni" value={metrics.bookingsToday} onClick={() => navigate("/admin/dashboard?tab=bookings")} testId="metric-bookings" />
              <MetricCard icon={LogIn} label="Arrivi" value={metrics.arrivalsToday} accent="text-emerald-700" testId="metric-arrivals" />
              <MetricCard icon={LogOut} label="Partenze" value={metrics.departuresToday} accent="text-sky-700" testId="metric-departures" />
              <MetricCard icon={Star} label="Recensioni" value={metrics.reviewsToday} accent="text-amber-600" testId="metric-reviews" />
              <MetricCard icon={XCircle} label="Cancellazioni" value={metrics.cancellations} accent="text-rose-600" testId="metric-cancellations" />
            </div>
          </div>

          {/* STATUS FILTER + UTILITY ICONS */}
          <div className="flex items-end justify-between flex-wrap gap-3 mt-4">
            <div className="md:max-w-xs w-full">
              <label className="text-[11px] text-[#635F59] uppercase tracking-wider font-semibold">Filtra per status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1 h-10" data-testid="group-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le strutture</SelectItem>
                  <SelectItem value="open">Aperto / Prenotabile</SelectItem>
                  <SelectItem value="closed">Chiuso / Non prenotabile</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 text-xs text-[#635F59]">
              <button className="inline-flex items-center gap-1 hover:text-[#0071C2]" data-testid="group-download"><Download className="w-3.5 h-3.5" />Scarica</button>
              <button className="inline-flex items-center gap-1 hover:text-[#0071C2]" data-testid="group-customize-data"><Columns3 className="w-3.5 h-3.5" />Personalizza i dati</button>
              <button className="inline-flex items-center gap-1 hover:text-[#0071C2]" data-testid="group-customize-view"><EyeOff className="w-3.5 h-3.5" />Personalizza la visualizzazione</button>
            </div>
          </div>

          {/* PROPERTIES TABLE */}
          <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-x-auto">
            <Table data-testid="group-properties-table">
              <TableHeader>
                <TableRow>
                  <TableHead>ID ↑</TableHead>
                  <TableHead>Struttura</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Arrivi 48h</TableHead>
                  <TableHead>Partenze 48h</TableHead>
                  <TableHead>Messaggi ospiti</TableHead>
                  <TableHead>Messaggi canale</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-[#635F59]">Caricamento...</TableCell></TableRow>
                ) : filteredProps.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-[#635F59]">Nessuna struttura corrisponde ai filtri.</TableCell></TableRow>
                ) : filteredProps.map((p) => (
                  <TableRow key={p.id} data-testid={`group-prop-${p.id}`}>
                    <TableCell className="font-mono text-[#0071C2] font-semibold">{p.id}</TableCell>
                    <TableCell>
                      <div className="font-medium text-[#0071C2] hover:underline cursor-pointer" onClick={() => navigate("/admin/channel-manager")}>{p.name}</div>
                      <div className="text-[10px] text-[#635F59]">{p.address}</div>
                    </TableCell>
                    <TableCell>
                      {p.status === "open" ? (
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" />
                          <span className="text-xs text-emerald-800">Aperto / Prenotabile</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5 text-rose-600" />
                          <span className="text-xs text-rose-800">Chiuso / Non prenotabile</span>
                          <a href="#" className="text-[10px] text-[#0071C2] hover:underline ml-1">Scopri perché</a>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-sm text-[#1E3A5F] font-semibold">{p.arrivals48}</TableCell>
                    <TableCell className="text-center text-sm text-[#1E3A5F] font-semibold">{p.departures48}</TableCell>
                    <TableCell>
                      {p.guestMsgs > 0 ? (
                        <Badge
                          variant="outline"
                          className="bg-[#0071C2] text-white border-[#0071C2] cursor-pointer hover:bg-[#005A9F] transition"
                          data-testid={`group-guest-msg-${p.id}`}
                          onClick={() => navigate("/admin/dashboard?tab=messages")}
                          title="Apri Messaggi"
                        >
                          <MessageSquare className="w-3 h-3 mr-1" />{p.guestMsgs}
                        </Badge>
                      ) : <span className="text-[#E8E2D9]">—</span>}
                    </TableCell>
                    <TableCell>
                      {p.channelMsgs > 0 ? (
                        <Badge
                          variant="outline"
                          className="bg-[#0071C2] text-white border-[#0071C2] cursor-pointer hover:bg-[#005A9F] transition"
                          onClick={() => navigate("/admin/dashboard?tab=messages")}
                          title="Apri Messaggi canale"
                        >
                          <MessageSquare className="w-3 h-3 mr-1" />{p.channelMsgs}
                        </Badge>
                      ) : <span className="text-[#E8E2D9]">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Footer hint about multi-struttura */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2 text-sm text-orange-900">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Pronto per il multi-struttura.</strong>{" "}
              Quando aggiungerai altre proprietà, compariranno automaticamente in questa tabella con gli stessi indicatori operativi.
              <button
                type="button"
                onClick={onAddProperty}
                className="text-[#AC5D45] hover:underline ml-1"
                data-testid="group-cta-add"
              >
                Aggiungi la prossima struttura →
              </button>
            </div>
          </div>
        </>
      )}

      {tab === "performance" && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-10 text-center text-[#635F59]">
          <Star className="w-10 h-10 text-[#E8E2D9] mx-auto mb-2" />
          <p className="text-sm">Riepilogo Performance disponibile dopo l'attivazione del multi-struttura.</p>
        </div>
      )}

      {tab === "settings" && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-10 text-center text-[#635F59]">
          <p className="text-sm">Impostazioni gruppo — disponibili dopo l'attivazione del multi-struttura.</p>
        </div>
      )}
    </div>
  );
}

const MetricCard = ({ icon: Icon, label, value, accent, testId, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="text-left bg-white border border-[#E8E2D9] hover:border-[#AC5D45] rounded-lg p-4 transition flex items-center gap-3"
  >
    <div className={`w-10 h-10 rounded-lg bg-[#FDFBF7] flex items-center justify-center ${accent || "text-[#1E3A5F]"}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="font-display text-2xl text-[#1E3A5F] leading-none">{value}</div>
      <div className="text-[11px] text-[#635F59] mt-0.5">{label}</div>
    </div>
  </button>
);
